/**
 * THE FIELD BUG'S DOM HALF (round 8 task 2).
 *
 * `viewport.ts` is the pure math; this is where `window.visualViewport`
 * actually gets read and where the fix for it — nudging the browser into
 * re-measuring — actually gets applied. It stays out of `MapBar`/`MapSheet`
 * on purpose: `tests/unit/map-chrome.test.ts` renders those with
 * `renderToStaticMarkup` and no `window`, so any DOM read here would break
 * them. It is mounted once, in `Shell` (`src/ui/App.tsx`), next to the
 * `--topbar-h` publisher this mirrors the shape of.
 *
 * ── The two moves ────────────────────────────────────────────────────────
 * `publishViewport` sets `--vv-shift` (the CSS the bar/sheet/veil/toast and
 * the photo viewer all read, theme.css) and toggles `keyboard-up` on `<html>`, from whatever
 * `window.visualViewport` says right now — the live wiring, so a resize or a
 * scroll of the visual viewport (the keyboard opening or closing) republishes
 * immediately.
 *
 * `settleViewport` is the fix for the STALE case: iOS does not always
 * re-fire a visualViewport event when the keyboard goes away and leaves the
 * layout viewport shifted, but it reliably re-measures on a real scroll. A
 * scroll of 1px and back is invisible and forces that re-measure; it is
 * scheduled `SETTLE_DELAY_MS` out because the shift is only real once the
 * keyboard has actually finished closing, not the instant the field blurs.
 * `openMap` (`SurveyMap.tsx`) calls it before the sheet opens, so its
 * `bottom: 0` sheet lands on the real bottom even if the surveyor just typed
 * a note; the focusout listener below calls it on every blur so the bar is
 * caught up before the surveyor's thumb ever needs it.
 */

import { useEffect } from 'react';

import { bottomShift, keyboardUp, type ViewportSample } from './viewport';

export const SETTLE_DELAY_MS = 300;

function sample(): ViewportSample {
  const vv = window.visualViewport;
  return {
    innerHeight: window.innerHeight,
    vvHeight: vv ? vv.height : window.innerHeight,
    vvOffsetTop: vv ? vv.offsetTop : 0,
    // A pinch-zoom halves `height` too; `viewport.ts` reads `scale` so a zoom
    // is never mistaken for a keyboard (final review of round 8).
    scale: vv ? vv.scale : 1,
  };
}

/** Read `window.visualViewport` now and publish `--vv-shift` / `keyboard-up` from it. */
export function publishViewport(): void {
  const s = sample();
  document.documentElement.style.setProperty('--vv-shift', `${String(bottomShift(s))}px`);
  document.documentElement.classList.toggle('keyboard-up', keyboardUp(s));
}

/**
 * The one pending settle. A form's worth of fields blurs many times in a
 * row, and each blur used to arm its own timer; one handle, re-armed, means
 * the nudge lands once, `SETTLE_DELAY_MS` after the LAST blur — and the hook
 * below can clear it on unmount so no timer outlives the shell.
 */
let pendingSettle: number | null = null;

/**
 * Force iOS to re-measure the visual viewport, then publish what it says.
 *
 * The 1px-and-back scroll is the nudge — it exists purely to make the
 * browser stop trusting a stale layout viewport. `scrollTo(0, y)` afterwards
 * rather than a second `scrollBy(0, -1)`: at maximum scroll (the last look's
 * notes field IS at the bottom) the `+1` is refused and a `-1` would move the
 * page a pixel up, so the page is put back where it was, not moved back by
 * the amount the nudge may not have moved it.
 */
export function settleViewport(): void {
  if (pendingSettle !== null) window.clearTimeout(pendingSettle);
  pendingSettle = window.setTimeout(() => {
    pendingSettle = null;
    const y = window.scrollY;
    window.scrollBy(0, 1);
    window.scrollTo(0, y);
    publishViewport();
  }, SETTLE_DELAY_MS);
}

/** Mounted once, in `Shell`: keeps `--vv-shift` and `keyboard-up` live, and settles on every field blur. */
export function useViewportGlue(): void {
  useEffect(() => {
    if (!window.visualViewport) return undefined;
    const vv = window.visualViewport;

    publishViewport();
    vv.addEventListener('resize', publishViewport);
    vv.addEventListener('scroll', publishViewport);

    const onFocusOut = (event: FocusEvent): void => {
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        settleViewport();
      }
    };
    document.addEventListener('focusout', onFocusOut);

    return () => {
      vv.removeEventListener('resize', publishViewport);
      vv.removeEventListener('scroll', publishViewport);
      document.removeEventListener('focusout', onFocusOut);
      if (pendingSettle !== null) {
        window.clearTimeout(pendingSettle);
        pendingSettle = null;
      }
    };
  }, []);
}
